import React from 'react';
import { formatText, HtmlText } from 'core/i18n';
import { checkUserInvestmentTypeAndReturnActiveOne } from 'utilities/userProfile';
import Patrimony from 'components/incomes/patrimony/patrimony';
import HasPEP from 'components/hasPEP/hasPEP';
import { TaxationQuestions } from 'components/taxationQuestions/taxationQuestions';
import AccountMoney from 'components/accountMoney/accountMoney';
import { accountUse as AccountUse } from 'components/accountUse/accountUse';
import BlockWrapper from 'components/blockWrapper/blockWrapper';
import { Cno } from 'components/professionTypes/cno';
import AnnualIncome from '../../questionBlock/AnnualIncome';
import ConditionalToggling from 'utilities/toggling/ConditionalToggling/ConditionalToggling';
import { recordEventCustomOnly } from 'utilities/uTagging';
import { CREDIT_CARD, LOAN } from 'constants/userProfile';
import { CompanyNameInput } from '../../components/companyNameInput';
import { FiocTgssConsent } from '../../components/FiocTgssConsent';
import MessageBox from 'components/commons/MessageBox/MessageBox';

export const DefaultQuestions = ({
    commonList,
    disabled,
    formActions,
    onHandleSave,
    shouldRenderTgssQuestion,
    showCompanyNameWarning,
    setShowCompanyNameWarning,
    userProfile,
    userProfileForm,
    ...props
}) => {
    const {
        cno1_profession_id,
        cno2_profession_id,
        employed_accountMoney,
        employed_accountUse,
        employed_annualIncomeAmount,
        employed_company,
        employed_hasPEP,
        investmentType,
        is_patrimony,
    } = userProfile;

    const userInvestmentType = checkUserInvestmentTypeAndReturnActiveOne(investmentType);

    return (
        <section className="fioc-account-type">
            <BlockWrapper position={0} visible={true} cancelDefaultFocusBehavior>
                <div className="fioc-account-type__cno">
                    <Cno
                        disabled={disabled}
                        handleSave={onHandleSave}
                        onActivityChange={() =>
                            recordEventCustomOnly({
                                categoriaEvento: 'interaccion en tu actividad',
                                accionEvento: 'sector actividad profesional',
                            })
                        }
                        onFieldChange={() =>
                            recordEventCustomOnly({
                                categoriaEvento: 'interaccion en tu actividad',
                                accionEvento: 'actividad profesional',
                            })
                        }
                    />
                </div>
            </BlockWrapper>

            <BlockWrapper
                position={1}
                visible={false}
                firstValue={employed_company || cno1_profession_id || cno2_profession_id}
            >
                <div className="fioc-account-type__company-name">
                    <CompanyNameInput
                        formActions={formActions}
                        handleOnFocus={() => {
                            setShowCompanyNameWarning(true);
                            recordEventCustomOnly({
                                accionEvento: 'nombre de la empresa',
                                categoriaEvento: 'interaccion en tu actividad',
                            });
                        }}
                        isValidated={() => {
                            setShowCompanyNameWarning(false);
                            onHandleSave('companyName', employed_company);
                        }}
                        label={formatText('field-companyName')}
                        profileModel="userProfile.employed_company"
                        userProfile={userProfile}
                        userProfileForm={userProfileForm}
                    />
                </div>
                {showCompanyNameWarning && (
                    <div className="fioc-account-type__messages__company-name">
                        <MessageBox
                            boxColor="yellow"
                            iconColor="warning-yellow"
                            titleText={formatText('field-companyNameWarningTitle')}
                            contentText={HtmlText(formatText('field-companyNameWarningDescription'))}
                        />
                    </div>
                )}
            </BlockWrapper>

            <BlockWrapper position={2} visible={false} shouldKeepVisibleAfter={employed_company}>
                <div className="fioc-account-type__anual-income">
                    <AnnualIncome
                        disabled={disabled}
                        formActions={formActions}
                        handleSave={onHandleSave}
                        model={'employed'}
                        onChange={() =>
                            recordEventCustomOnly({
                                accionEvento: 'ingresos',
                                categoriaEvento: 'interaccion en tu actividad',
                            })
                        }
                        optionsList={commonList}
                        userProfile={userProfile}
                        {...props}
                    />
                </div>
            </BlockWrapper>

            <ConditionalToggling condition={!userInvestmentType[CREDIT_CARD] && !userInvestmentType[LOAN]}>
                <>
                    <BlockWrapper position={3} visible={false} firstValue={employed_annualIncomeAmount}>
                        <div className="fioc-account-type__patrimony">
                            <Patrimony
                                handleSave={onHandleSave}
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                formActions={formActions}
                                disabled={disabled}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper
                        position={4}
                        visible={false}
                        firstValue={is_patrimony || (userInvestmentType[CREDIT_CARD] && employed_annualIncomeAmount)}
                    >
                        <div className="fioc-account-type__has-pep">
                            <HasPEP
                                disabled={disabled}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                profileModel="userProfile.employed_hasPEP"
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={5} visible={false} firstValue={employed_hasPEP}>
                        <div className="fioc-account-type__has-pep">
                            <AccountMoney
                                formActions={formActions}
                                handleSave={onHandleSave}
                                profileModel="userProfile.employed_accountMoney"
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={6} visible={false} firstValue={employed_accountMoney}>
                        <div className="fioc-account-type__account-use">
                            <AccountUse
                                profileModel="userProfile.employed_accountUse"
                                accountTypes={['personal', 'other']}
                                hasPEP={userProfile['employed_hasPEP']}
                                userProfile={userProfile}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                userProfileForm={userProfileForm}
                                disabled={disabled}
                                {...props}
                            />
                        </div>
                    </BlockWrapper>

                    <BlockWrapper position={7} visible={false} firstValue={employed_accountUse}>
                        <div className="fioc-account-type__taxation-questions">
                            <TaxationQuestions
                                disabled={disabled}
                                formActions={formActions}
                                handleSave={onHandleSave}
                                userProfile={userProfile}
                                userProfileForm={userProfileForm}
                            />
                        </div>
                    </BlockWrapper>

                    {shouldRenderTgssQuestion && (
                        <BlockWrapper position={7} visible={false} firstValue={employed_accountUse}>
                            <div className="fioc-account-type__tgss-consent">
                                <FiocTgssConsent
                                    formActions={formActions}
                                    userProfile={userProfile}
                                    userProfileForm={userProfileForm}
                                />
                            </div>
                        </BlockWrapper>
                    )}
                </>
            </ConditionalToggling>
        </section>
    );
};
